import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button, ConfirmDialog, PageHeader, Spinner } from '../components/ui';
import EventBuilderConversation from '../components/EventBuilderConversation';
import EventBuilderProposal from '../components/EventBuilderProposal';
import EventBuilderAttendeePreview from '../components/EventBuilderAttendeePreview';
import { useAdminAuth } from '../hooks/useAdminAuth';
import {
  eventBuilderService,
  type EventBuilderDraft,
  type EventBuilderTemplate,
} from '../services/eventBuilderService';

type Panel = 'conversation' | 'proposal' | 'preview';

function missingRequirementCount(draft: EventBuilderDraft | null) {
  if (!draft) return 6;
  const requirements = draft.requirements;
  return [requirements.name, requirements.date_start, requirements.date_end, requirements.timezone, requirements.access]
    .filter((value) => !value).length
    + (requirements.online === true || requirements.location ? 0 : 1);
}

export default function AIEventBuilderWorkspace() {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const { activeWorkspaceId, canCreateEvents } = useAdminAuth();
  const [draft, setDraft] = useState<EventBuilderDraft | null>(null);
  const [templates, setTemplates] = useState<EventBuilderTemplate[]>([]);
  const [loading, setLoading] = useState(Boolean(draftId));
  const [pending, setPending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>('conversation');

  useEffect(() => {
    let active = true;
    if (!draftId || draft?.id === draftId) return () => { active = false; };
    setLoading(true);
    eventBuilderService.get(draftId)
      .then((value) => { if (active) setDraft(value); })
      .catch((error: unknown) => { if (active) setNotice(error instanceof Error ? error.message : 'Unable to load draft'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [draftId]);

  useEffect(() => {
    let active = true;
    if (!activeWorkspaceId) return () => { active = false; };
    eventBuilderService.listTemplates()
      .then((value) => { if (active) setTemplates(value); })
      .catch((error: unknown) => { if (active) setNotice(error instanceof Error ? error.message : 'Unable to load templates'); });
    return () => { active = false; };
  }, [activeWorkspaceId]);

  async function send(message: string, recommendNow = false): Promise<boolean> {
    if (!activeWorkspaceId) {
      setNotice('Choose an approved workspace first.');
      return false;
    }
    if (!draft && !canCreateEvents) {
      setNotice('Only a workspace admin can create an event.');
      return false;
    }
    setPending(true);
    setPendingMessage(message);
    setNotice(null);
    try {
      if (!draft) {
        const created = await eventBuilderService.start(activeWorkspaceId, message);
        setDraft(created);
        navigate(`/ai-event-builder/${created.id}`, { replace: true });
      } else {
        setDraft(await eventBuilderService.sendTurn(draft.id, draft.revision, message, recommendNow));
      }
      return true;
    } catch (error) {
      if (draft && error instanceof Error && error.message.includes('409')) {
        setDraft(await eventBuilderService.get(draft.id));
        setNotice('Another tab updated this draft. The latest version is loaded; your message was kept.');
      } else {
        setNotice(error instanceof Error ? error.message : 'The builder could not save this message.');
      }
      return false;
    } finally {
      setPending(false);
      setPendingMessage(null);
    }
  }

  async function updateTemplate(
    action: (current: EventBuilderDraft) => Promise<EventBuilderDraft>,
  ) {
    if (!draft) return;
    setTemplateBusy(true);
    setNotice(null);
    try {
      setDraft(await action(draft));
    } catch (error) {
      if (error instanceof Error && error.message.includes('409')) {
        setDraft(await eventBuilderService.get(draft.id));
        setNotice('Another tab updated this draft. The latest version is loaded; try the action again.');
      } else {
        setNotice(error instanceof Error ? error.message : 'The Builder could not update this design.');
      }
    } finally {
      setTemplateBusy(false);
    }
  }

  async function convert() {
    if (!draft) return;
    setConverting(true);
    setNotice(null);
    try {
      const event = await eventBuilderService.convert(draft.id, draft.revision);
      navigate(
        !editingEvent && event.effective_content_ingestion_enabled
          ? `/events/${event.id}/content/setup`
          : `/events/${event.id}/app-setup`,
      );
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'The event draft could not be created.');
    } finally {
      setConverting(false);
      setConfirming(false);
    }
  }

  const selectedKey = draft?.template_state.selected_template?.key;
  const recommendedKey = draft?.template_state.recommended_template?.key;
  const designTemplate = templates.find((item) => item.key === (selectedKey ?? recommendedKey));
  const designLabel = draft?.template_state.mode === 'custom'
    ? 'Custom AI design'
    : designTemplate
      ? `${designTemplate.name}${selectedKey ? '' : ' recommended'}`
      : 'No design selected';
  const enabledModuleCount = Object.values(draft?.blueprint.app_modules ?? {})
    .filter((module) => module.enabled === true).length;
  const missingCount = missingRequirementCount(draft);
  const readiness = draft?.template_state.mode === 'discovery' && missingCount === 0
    ? 'Choose a design'
    : missingCount === 0
      ? draft?.created_event_id ? 'Ready to apply changes' : 'Ready to create'
      : `${missingCount} ${missingCount === 1 ? 'detail' : 'details'} missing`;
  const editingEvent = Boolean(draft?.created_event_id);

  const proposal = <EventBuilderProposal
    draft={draft}
    templates={templates}
    converting={converting}
    templateBusy={templateBusy || pending}
    onPreviewTemplate={(key) => {
      setReviewing(false);
      void updateTemplate((current) => eventBuilderService.previewTemplate(current.id, current.revision, key));
    }}
    onCustomize={() => {
      setReviewing(false);
      void updateTemplate((current) => eventBuilderService.customize(current.id, current.revision));
    }}
    onRecommendNow={() => { void send('Recommend now', true); }}
    onCreate={() => setConfirming(true)}
  />;

  if (loading) return <div className="flex justify-center py-20"><Spinner className="h-6 w-6 text-gray-500" /></div>;

  return <div className="p-8">
    <PageHeader title="AI Event Builder" description="Chat, review the validated proposal, and create an editable event draft." breadcrumb={<Link to="/ai-event-builder" className="hover:text-sky-400">AI Event Builder</Link>} />
    {draft?.created_event_id && <div className="mb-4 flex items-center justify-between gap-4 rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 text-sm">
      <p className="font-medium text-sky-100">Editing {draft.requirements.name || 'linked event'}</p>
      <Link to={`/events/${draft.created_event_id}/app-setup`} className="flex-shrink-0 font-medium text-sky-400 hover:text-sky-300">Open event workspace</Link>
    </div>}
    {notice && <p role="status" className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-100">{notice}</p>}
    <div className="mb-4 flex gap-2 min-[1100px]:hidden" role="tablist" aria-label="Builder panels">
      {(['conversation', 'proposal', 'preview'] as Panel[]).map((item) => <button key={item} type="button" role="tab" aria-selected={panel === item} onClick={() => setPanel(item)} className={`rounded-lg px-3 py-2 text-sm capitalize ${panel === item ? 'bg-sky-500 text-white' : 'bg-gray-800 text-gray-400'}`}>{item}</button>)}
    </div>
    <section aria-label="Design status" className="mb-4 hidden items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 px-4 py-3 min-[1100px]:flex">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-white">{designLabel}</p>
        <p className="mt-0.5 text-xs text-gray-500">{enabledModuleCount} {enabledModuleCount === 1 ? 'module' : 'modules'} enabled <span aria-hidden="true">·</span> {readiness}</p>
      </div>
      <Button size="sm" variant="secondary" disabled={!draft} onClick={() => setReviewing(true)}>Review design</Button>
    </section>
    <div className="grid gap-4 min-[1100px]:grid-cols-[minmax(420px,1fr)_440px]">
      <div className={panel === 'conversation' ? '' : 'max-[1099px]:hidden'}><EventBuilderConversation draft={draft} pending={pending} pendingMessage={pendingMessage} onSend={send} onRecommendNow={() => { void send('Recommend now', true); }} onUpload={(upload) => setDraft((current) => current ? { ...current, uploads: [...current.uploads.filter((item) => item.id !== upload.id), upload] } : current)} /></div>
      <section aria-label="Attendee preview" className={`${panel === 'preview' ? '' : 'max-[1099px]:hidden'} min-h-[620px] rounded-2xl border border-gray-800 bg-gray-900 p-5`}>
        <h2 className="font-semibold text-white">Preview</h2>
        <p className="mb-4 mt-1 text-xs text-gray-500">Live attendee UI with sample content. No attendee data is created.</p>
        <EventBuilderAttendeePreview draft={draft} />
      </section>
      {!reviewing && <div className={`${panel === 'proposal' ? '' : 'max-[1099px]:hidden'} min-[1100px]:hidden`}>{proposal}</div>}
    </div>
    <Dialog.Root open={reviewing} onOpenChange={setReviewing}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-gray-800 bg-gray-950 p-4 shadow-2xl">
          <Dialog.Title className="sr-only">Review design</Dialog.Title>
          <Dialog.Description className="sr-only">Review the proposed event experience and create the event draft.</Dialog.Description>
          <Dialog.Close asChild>
            <button type="button" aria-label="Close design review" className="absolute right-7 top-7 z-10 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-800 hover:text-white"><X size={18} /></button>
          </Dialog.Close>
          {proposal}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
    <ConfirmDialog open={confirming} onClose={() => setConfirming(false)} onConfirm={() => void convert()} loading={converting} intent="primary" title={editingEvent ? 'Apply changes to this event?' : 'Create this event draft?'} description={editingEvent ? 'Apply this AI-assisted draft to the linked event?' : 'This creates one normal event draft in the active workspace. You can continue editing it in App Setup.'} confirmLabel={editingEvent ? 'Apply changes' : 'Create event'} />
  </div>;
}
